import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import doneCircleGreenIcon from 'assets/images/icons/done-circle-green-filled.svg';
import eyeIcon from 'assets/images/icons/eye-off.svg';
import FormButton from 'components/form-components/FormButton';
import FormLoadingButton from 'components/form-components/FormLoadingButton';
import infoCircleRedIcon from 'assets/images/icons/info-circle-red.svg';
import MobileNumberField from 'components/form-components/MobileNumberField';
import PasswordField from 'components/form-components/PasswordField';
import phoneIcon from 'assets/images/icons/phone.svg';
import TextField from 'components/form-components/TextField';
import { saveProfileSchema } from './utils';
import { encryptPassword } from 'pages/auth-pages/utils';
import { CallAPI } from 'actions/General';

const ProfileDetails = ({ userProfileDetails, getUserProfileDetails }) => {
  const { t } = useTranslation();
  const linkValidityInSec = 180;
  const validityIdsKey = 'verificationEmailSentIds';

  const [values, setValues] = useState({
    firstName: userProfileDetails.firstName,
    lastName: userProfileDetails.lastName,
    email: userProfileDetails.email ?? '',
    countryCode: userProfileDetails.countryCode,
    mobileNumber: userProfileDetails.mobileNumber,
    joinedDate: new Date(userProfileDetails.companies[0].startDate).toLocaleDateString(),
    password: '',
  });
  const [emailVerified, setEmailVerified] = useState(userProfileDetails.emailVerified ?? false);
  const [groupList] = useState(userProfileDetails.companies[0].userGroups);
  const [verifyDisabled, setVerifyDisabled] = useState(false);
  const [disabledRemTime, setDisabledRemTime] = useState(0);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);

  const handleChange = (field, value) => {
    setTouched({ ...touched, [field]: true });
    setErrors({ ...errors, [field]: undefined });
    setValues({ ...values, [field]: value });
  };

  const handleEmailChange = (field, value) => {
    setTouched({ ...touched, [field]: true });
    setErrors({ ...errors, [field]: undefined });
    const savedEmail = userProfileDetails.email ?? '';
    const savedVerifiedStatus = userProfileDetails.emailVerified ?? false;

    setValues({ ...values, [field]: value });
    if (value === '') setEmailVerified(false);
    else if (value === savedEmail) setEmailVerified(savedVerifiedStatus);
    else setEmailVerified(false);
  };

  const submit = async () => {
    setTouched({});
    try {
      await saveProfileSchema.validate(values, { abortEarly: false });

      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email !== '' ? values.email : null,
      };
      if (values.password.length) {
        const encryptedPassword = await encryptPassword(values.password);
        payload.password = encryptedPassword;
      }

      const response = await CallAPI('UPDATE_OWN_PROFILE_DETAILS', payload, null, setSaveLoading);
      if (response.status) {
        toast.success('Profile details updated successfully');
        getUserProfileDetails();
      } else {
        toast.error(response.error);
      }
    } catch (error) {
      const schemaErrors = {};
      error.inner?.forEach((err) => {
        schemaErrors[err.path] = err.message;
      });

      setErrors(schemaErrors);
    }
  };

  const checkVerifyDisabled = () => {
    const currentEmailSentIds = JSON.parse(localStorage.getItem(validityIdsKey) ?? '{}');

    const time = currentEmailSentIds[userProfileDetails._id] ?? 0;
    const currentTimestamp = Math.floor(Date.now() / 1000);
    const isDisabled = !(time === 0 || currentTimestamp - time > linkValidityInSec);

    setVerifyDisabled(isDisabled);
    setDisabledRemTime(time === 0 ? 0 : linkValidityInSec - (currentTimestamp - time));
  };

  const sendVerificationEmail = async () => {
    const currentEmailSentIds = JSON.parse(localStorage.getItem(validityIdsKey) ?? '{}');

    const time = currentEmailSentIds[userProfileDetails._id] ?? 0;
    const currentTimestamp = Math.floor(Date.now() / 1000);
    if (time === 0 || currentTimestamp - time > linkValidityInSec) {
      const verificationEmailSentIds = {
        ...currentEmailSentIds,
        [userProfileDetails._id]: currentTimestamp,
      };

      const response = await CallAPI('SEND_EMAIL_VERIFICATION_CODE', {}, null, setVerifyLoading);
      if (response.status) {
        toast.success('Verification email sent successfully');
        localStorage.setItem(validityIdsKey, JSON.stringify(verificationEmailSentIds));
      } else toast.error(response.error);
    }
  };

  const clearResendTimer = () => {
    const currentEmailSentIds = JSON.parse(localStorage.getItem(validityIdsKey) ?? '{}');

    if (userProfileDetails.emailVerified) {
      delete currentEmailSentIds[userProfileDetails._id];
      if (Object.keys(currentEmailSentIds).length)
        localStorage.setItem(validityIdsKey, JSON.stringify(currentEmailSentIds));
      else localStorage.removeItem(validityIdsKey);
    }
  };

  useEffect(() => {
    clearResendTimer();
    checkVerifyDisabled();

    const intervalId = setInterval(() => {
      checkVerifyDisabled();
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  return (
    <div>
      <div className='border-grey-1-h-1 rounded-sm p-4 mb-4'>
        <span className='theme-font-inter-medium theme-text-grey-1 theme-size-1_1 d-block mb-2'>
          {t('text_personal_detail')}
        </span>
        <div className='d-flex gap-3 mb-3'>
          <TextField
            name='firstName'
            displayName='field_first_name'
            placeholder='field_first_name'
            value={values.firstName}
            error={errors.firstName}
            touched={touched.firstName}
            handleChange={handleChange}
            classes='w-100'
            shrinkFieldOnly
          />
          <TextField
            name='lastName'
            displayName='field_last_name'
            placeholder='field_last_name'
            value={values.lastName}
            error={errors.lastName}
            touched={touched.lastName}
            handleChange={handleChange}
            classes='w-100'
            shrinkFieldOnly
          />
        </div>
        <div className='d-flex gap-3 mb-3'>
          <TextField
            name='email'
            displayName='field_email'
            placeholder='field_email'
            value={values.email}
            error={errors.email}
            touched={touched.email}
            handleChange={handleEmailChange}
            classes='w-100'
            shrinkFieldOnly
            icon={emailVerified ? doneCircleGreenIcon : infoCircleRedIcon}
            tooltipContent={
              !values.email.trim()
                ? 'text_email_is_required'
                : emailVerified
                ? 'text_email_verified'
                : 'text_email_not_verified'
            }
          />
          <MobileNumberField
            fieldProps={{ name: 'mobileNumber', placeholder: '123123123' }}
            displayName='field_phone_number'
            icon={phoneIcon}
            value={{ countryCode: values.countryCode, mobileNumber: values.mobileNumber }}
            error={errors.mobileNumber}
            touched={touched.mobileNumber}
            handleChange={handleChange}
            classes='w-100'
            shrinkFieldOnly
            disabled
          />
        </div>
        <div className='d-flex gap-3 mb-4'>
          <TextField
            name='joinedDate'
            displayName='field_joined_date'
            placeholder='field_joined_date'
            value={values.joinedDate ?? ''}
            handleChange={handleChange}
            disabled
            classes='w-100'
            shrinkFieldOnly
          />
          <PasswordField
            name='password'
            displayName='field_password'
            placeholder='field_enter_password'
            icon={eyeIcon}
            value={values.password}
            error={errors.password}
            touched={touched.password}
            handleChange={handleChange}
            autoComplete={false}
            classes='w-100'
            shrinkFieldOnly
          />
        </div>
        <div>
          <label className='mb-3 theme-size-1_3'>Groups ( {groupList.length} )</label>
          <div className='d-flex gap-3 flex-wrap'>
            {groupList.map((group, index) => (
              <div key={index} className='py-2 px-3 theme-background-white-3 rounded-pill'>
                <label>{group.groupName}</label>
              </div>
            ))}
          </div>
        </div>
      </div>
      {verifyDisabled && disabledRemTime !== 0 && (
        <div className='text-right'>
          <label className='theme-text-red-2'>
            {`You can resend verification email after ${Math.floor(
              disabledRemTime / 60,
            )} : ${Math.floor(disabledRemTime % 60)} secs`}
          </label>
        </div>
      )}
      <div className='mt-3 w-100 d-flex justify-content-end gap-3'>
        <FormButton text='button_cancel' variant='white-1' />
        {!emailVerified && (
          <FormLoadingButton
            loading={verifyLoading}
            onClick={sendVerificationEmail}
            text='button_verify_email'
            disabled={!values.email || verifyDisabled || userProfileDetails.email !== values.email}
            variant='green-1'
          />
        )}
        <FormLoadingButton
          loading={saveLoading}
          onClick={submit}
          text='button_save'
          variant='green-1'
        />
      </div>
    </div>
  );
};

ProfileDetails.propTypes = {
  userProfileDetails: PropTypes.object.isRequired,
  getUserProfileDetails: PropTypes.func.isRequired,
};

export default ProfileDetails;
